import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import httpClient from '../utils/HttpClient';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setAdsAction } from '../modules/dataWebsite/actions';
import { IoClose } from 'react-icons/io5';

// Helper to get local date string YYYY-MM-DD
export const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Map route pathname to the section key used by the backend options
export const getMatchedSection = (pathname) => {
  if (!pathname) return null;

  if (pathname.startsWith('/news')) {
    return 'news';
  }

  if (
    pathname.startsWith('/product') 
    // ||
    // pathname.startsWith('/cart') ||
    // pathname.startsWith('/checkout') ||
    // pathname.startsWith('/order-confirmation') ||
    // pathname.startsWith('/my-orders') ||
    // pathname.startsWith('/purchase-history') ||
    // pathname.startsWith('/address-list') ||
    // pathname.startsWith('/add-address') ||
    // pathname.startsWith('/edit-address')
  ) {
    return 'product';
  }

  if (
    pathname.startsWith('/magazine') 
    // ||
    // pathname.startsWith('/fav-magazine') ||
    // pathname.startsWith('/like-magazine')
  ) {
    return 'magazine';
  }

  if (pathname.startsWith('/fashion') || pathname.startsWith('/gallery')) {
    return 'fashion';
  }

  if (
    pathname.startsWith('/technical') ||
    pathname.startsWith('/education') ||
    pathname.startsWith('/online-class') 
    // ||
    // pathname.startsWith('/online-class-member') ||
    // pathname.startsWith('/like-course') ||
    // pathname.startsWith('/purchased-course')
  ) {
    return 'technology';
  }

  return null;
};

// Check if a targetSection config matches the current pathname
export const isSectionMatch = (targetSection, pathname) => {
  if (!targetSection) return false;

  const targets = Array.isArray(targetSection)
    ? targetSection
    : [targetSection];

  if (targets.length === 0) return false;

  const currentSection = getMatchedSection(pathname);
  if (!currentSection) return false;

  if (targets.includes('all')) return true;

  return targets.includes(currentSection);
};

export default function PopupAdModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const adsData = useSelector((state) => state.dataWebsiteReducer?.adsData) || [];

  const [isAdModalOpen, setIsAdModalOpen] = useState(false);
  // const [countdown, setCountdown] = useState(5);
  const [currentAd, setCurrentAd] = useState(null);
  // const adTimerRef = useRef(null);

  useEffect(() => {
    httpClient
      .get('/ads')
      .then((res) => {
        if (res?.data?.data) {
          const adData = res.data.data;
          const adList = Array.isArray(adData) ? adData : [adData];
          setAdsAction(adList);
        }
      })
      .catch((err) => console.error('fetch popup ads error', err));
  }, []);

  const authUser = useSelector((state) => state.authReducer?.user);
  const memberId = authUser?.memberId || authUser?._id || authUser?.id || 'guest';

  useEffect(() => {
    const list = Array.isArray(adsData) ? adsData : (adsData ? [adsData] : []);
    const matchingAd = list.find((ad) => {
      if (!ad || ad.statusSectionAds === false) return false;
      const sectionAds = ad.sectionAds;
      if (!sectionAds || !sectionAds.picUrl) return false;
      return isSectionMatch(sectionAds.targetSection, location.pathname);
    });

    if (matchingAd) {
      const currentSection = getMatchedSection(location.pathname) || 'general';
      const adId = matchingAd._id || matchingAd.id || 'ad';
      const storageKey = `popup_ad_shown_${memberId}_${currentSection}_${adId}`;
      const today = getTodayDateString();

      const lastShownDate = localStorage.getItem(storageKey);
      if (lastShownDate !== today) {
        localStorage.setItem(storageKey, today);
        setCurrentAd(matchingAd);
        setIsAdModalOpen(true);
        // setCountdown(5);
      }
    } else {
      setIsAdModalOpen(false);
    }
  }, [location.pathname, adsData, memberId]);

  // useEffect(() => {
  //   if (isAdModalOpen) {
  //     adTimerRef.current = setInterval(() => {
  //       setCountdown((prev) => {
  //         if (prev <= 1) {
  //           clearInterval(adTimerRef.current);
  //           setIsAdModalOpen(false);
  //           return 0;
  //         }
  //         return prev - 1;
  //       });
  //     }, 1000);
  //   } else {
  //     clearInterval(adTimerRef.current);
  //   }
  //   return () => clearInterval(adTimerRef.current);
  // }, [isAdModalOpen]);

  const handleAdClick = () => {
    const sectionAds = currentAd?.sectionAds;
    if (!sectionAds?.linkUrl) return;

    setIsAdModalOpen(false);
    const rawUrl = sectionAds.linkUrl.trim();

    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      try {
        const parsed = new URL(rawUrl);
        const internalPrefixes = [
          '/news',
          '/product',
          '/product-knowledge',
          '/technical',
          '/online-class',
          '/online-class-member',
          '/fashion',
          '/gallery',
          '/magazine',
          '/events',
          '/shareform'
        ];
        if (internalPrefixes.some(p => parsed.pathname === p || parsed.pathname.startsWith(p + '/'))) {
          navigate(`${parsed.pathname}${parsed.search}${parsed.hash}`);
          return;
        }
      } catch (e) {
        // Fall through
      }
      if (sectionAds.linkType === 'external' || !sectionAds.linkType) {
        window.open(rawUrl, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    if (sectionAds.linkType === 'internal') {
      if (rawUrl.startsWith('/')) {
        navigate(rawUrl);
      } else if ([
        'news',
        'product-knowledge',
        'technical',
        'product',
        'online-class',
        'online-class-member',
        'fashion',
        'gallery',
        'magazine',
        'shareform',
        'events'
      ].some(prefix => rawUrl.startsWith(prefix))) {
        navigate(`/${rawUrl}`);
      } else {
        navigate(`/product/${rawUrl}`);
      }
    } else {
      const extUrl = (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) ? rawUrl : `https://${rawUrl}`;
      window.open(extUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const sectionAds = currentAd?.sectionAds;
  if (!sectionAds || !sectionAds.picUrl) return null;

  return (
    <Modal
      open={isAdModalOpen}
      footer={null}
      closable={false}
      onCancel={() => setIsAdModalOpen(false)}
      width={600}
      styles={{
        body: { padding: 0, background: 'transparent' },
        content: { background: 'transparent', boxShadow: 'none' },
      }}
      centered
    >
      <div className="relative cursor-pointer" onClick={handleAdClick}>
        <img
          src={sectionAds.picUrl}
          alt="ad"
          className="w-full h-auto object-cover rounded-lg"
          style={{ maxHeight: '500px' }}
        />
        <button
          type="button"
          aria-label="Close"
          className="absolute top-2.5 right-2.5 w-8 h-8 bg-black/50 hover:bg-black/75 text-white rounded-full z-10 flex items-center justify-center transition-colors cursor-pointer border-0 outline-none"
          onClick={(e) => {
            e.stopPropagation();
            setIsAdModalOpen(false);
          }}
        >
          <IoClose className="text-xl" />
        </button>

        {sectionAds.linkUrl && (
          <div className="absolute bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3.5 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm z-10 flex items-center justify-center gap-1 hover:bg-black/70 transition-colors whitespace-nowrap max-w-[90%] shadow-sm">
            <span>{t('text_jump_details')}</span>
            <span>&gt;</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
